import { isAxiosError } from 'axios'

const DEFAULT_ERROR_MESSAGE = 'Something went wrong'

/**
 * Whether an error is a wallet insufficient-balance rejection (HTTP 402).
 * The billing balance gate answers 402 when an action can't be afforded.
 */
export function isInsufficientBalanceError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 402
}

/**
 * Extract error messages from API response data.
 * Handles Django REST Framework error formats.
 */
function extractMessagesFromData(data: unknown): string[] {
  if (!data) {
    return [DEFAULT_ERROR_MESSAGE]
  }

  // String response
  if (typeof data === 'string') {
    return [data]
  }

  // Array of strings
  if (Array.isArray(data) && data.length && typeof data[0] === 'string') {
    return data
  }

  // Object with field errors or detail
  if (typeof data === 'object' && data !== null) {
    const record = data as Record<string, unknown>

    // Check for detail field first (common DRF pattern)
    if ('detail' in record && typeof record.detail === 'string') {
      return [record.detail]
    }

    // Field-specific errors (e.g., { amount: ["This field is required"] })
    const fieldErrors = Object.entries(record)
      .filter(([key]) => key !== 'detail')
      .map(([key, value]) => {
        const errorMsg = Array.isArray(value) ? value.join(', ') : String(value)
        return `${key}: ${errorMsg}`
      })
      .filter((msg) => msg.length > 0)

    if (fieldErrors.length > 0) {
      return fieldErrors
    }
  }

  return [DEFAULT_ERROR_MESSAGE]
}

/**
 * Get error messages as an array from an Error object.
 */
export function getErrorMessages(e: Error, defaultMessage = DEFAULT_ERROR_MESSAGE): string[] {
  if (isAxiosError(e)) {
    const { data } = e.response ?? {}
    return extractMessagesFromData(data)
  }
  return [e.message || defaultMessage]
}

export interface ParsedBackendErrors {
  /** Field-specific messages, keyed by camelCased backend field name. */
  fieldErrors: Record<string, string[]>
  /** Non-field messages (`detail`, `non_field_errors`, network/unknown errors). */
  generalErrors: string[]
}

const NON_FIELD_KEYS = new Set(['detail', 'non_field_errors', 'nonFieldErrors'])

function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())
}

function toMessages(value: unknown): string[] {
  const raw = Array.isArray(value) ? value.map((v) => String(v)) : [String(value)]
  return raw.filter((m) => m.length > 0)
}

/**
 * Parse a backend error into field-specific and general buckets.
 * Field errors are keyed by the camelCased backend field name so callers can
 * route each message to the matching form field; `detail` / `non_field_errors`
 * and any non-object payloads land in `generalErrors`.
 */
export function parseBackendErrors(
  error: unknown,
  defaultMessage = 'An unexpected error occurred. Please try again.',
): ParsedBackendErrors {
  const fieldErrors: Record<string, string[]> = {}
  const generalErrors: string[] = []

  if (!isAxiosError(error)) {
    generalErrors.push(error instanceof Error ? error.message || defaultMessage : defaultMessage)
    return { fieldErrors, generalErrors }
  }

  const data = error.response?.data
  if (data == null) {
    generalErrors.push(error.message || defaultMessage)
    return { fieldErrors, generalErrors }
  }
  if (typeof data === 'string') {
    generalErrors.push(data || defaultMessage)
    return { fieldErrors, generalErrors }
  }
  if (Array.isArray(data)) {
    generalErrors.push(...toMessages(data))
  } else if (typeof data === 'object') {
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      const messages = toMessages(value)
      if (!messages.length) continue
      if (NON_FIELD_KEYS.has(key)) {
        generalErrors.push(...messages)
      } else {
        fieldErrors[snakeToCamel(key)] = messages
      }
    }
  }

  if (!generalErrors.length && !Object.keys(fieldErrors).length) {
    generalErrors.push(defaultMessage)
  }
  return { fieldErrors, generalErrors }
}

/**
 * Extract a single error message string from backend response.
 * Handles Django REST Framework error formats including field-specific errors.
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage = 'An unexpected error occurred. Please try again.',
): string {
  if (error instanceof Error) {
    return getErrorMessages(error, defaultMessage).join('; ')
  }
  return defaultMessage
}
